import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { supabase, supabaseAdmin } from '@/lib/supabase';
import type {
  Organization,
  OrganizationUpdate,
  ClientWithSubscription,
  SubscriptionInfo,
  SubscriptionPlan,
  BillingHistory,
  BillingEvent,
} from '@/lib/database.types';

type SubscriptionWithPlan = SubscriptionInfo & { plan: SubscriptionPlan | null };

// Match subscription_info to its plan from subscription_plans by plan_name
function attachPlan(
  sub: SubscriptionInfo | null,
  plans: SubscriptionPlan[]
): SubscriptionWithPlan | null {
  if (!sub) return null;
  const plan = plans.find((p) => p.name === sub.plan_name) || null;
  return { ...sub, plan };
}

export function useClients() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['clients'],
    queryFn: async () => {
      // Fetch organizations, subscription_info, and plans in parallel
      // supabaseAdmin bypasses RLS so admin users can see all client data
      const [orgsResult, subsResult, plansResult] = await Promise.all([
        supabaseAdmin
          .from('organizations')
          .select('*')
          .order('created_at', { ascending: false }),
        supabaseAdmin.from('subscription_info').select('*'),
        supabaseAdmin
          .from('subscription_plans')
          .select('*')
          .eq('is_active', true as never)
          .order('sort_order', { ascending: true }),
      ]);

      if (orgsResult.error) throw orgsResult.error;
      if (subsResult.error) throw subsResult.error;
      if (plansResult.error) throw plansResult.error;

      const orgs = (orgsResult.data || []) as unknown as Organization[];
      const subs = (subsResult.data || []) as unknown as SubscriptionInfo[];
      const plans = (plansResult.data || []) as unknown as SubscriptionPlan[];

      return orgs.map((org) => {
        const sub = subs.find((s) => s.organization_id === org.id) || null;
        return {
          ...org,
          subscription: attachPlan(sub, plans),
        } as ClientWithSubscription;
      });
    },
  });

  // Realtime subscription for organizations and subscription_info changes
  useEffect(() => {
    const channel = supabase
      .channel('admin-clients-realtime')
      .on(
        'postgres_changes' as any,
        { event: '*', schema: 'public', table: 'organizations' },
        () => {
          queryClient.invalidateQueries({ queryKey: ['clients'] });
          queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        }
      )
      .on(
        'postgres_changes' as any,
        { event: '*', schema: 'public', table: 'subscription_info' },
        () => {
          queryClient.invalidateQueries({ queryKey: ['clients'] });
          queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  return query;
}

export function useClient(slug: string) {
  return useQuery({
    queryKey: ['clients', slug],
    queryFn: async () => {
      // Fetch the organization by slug
      const { data: orgData, error: orgError } = await supabaseAdmin
        .from('organizations')
        .select('*')
        .eq('slug', slug as never)
        .single();

      if (orgError) throw orgError;
      const org = orgData as unknown as Organization;

      // Fetch subscription_info for this org
      const { data: subData } = await supabaseAdmin
        .from('subscription_info')
        .select('*')
        .eq('organization_id', org.id as never)
        .single();

      const sub = (subData as unknown as SubscriptionInfo) || null;

      // Fetch plans to resolve plan_name -> plan details
      const { data: plansData } = await supabaseAdmin
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true as never);

      const plans = (plansData || []) as unknown as SubscriptionPlan[];

      return {
        ...org,
        subscription: attachPlan(sub, plans),
      } as ClientWithSubscription;
    },
    enabled: !!slug,
  });
}

export function useUpdateClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: OrganizationUpdate }) => {
      const { data, error } = await supabase
        .from('organizations')
        .update(updates as never)
        .eq('id', id as never)
        .select()
        .single();

      if (error) throw error;
      return data as unknown as Organization;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['clients', data.slug] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useSetPurchasingManagerAccess() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      organizationId,
      clientSlug,
      enabled,
    }: {
      organizationId: string;
      clientSlug: string;
      enabled: boolean;
    }) => {
      const { data, error } = await supabase.rpc(
        'set_organization_purchasing_manager_access' as never,
        {
          p_organization_id: organizationId,
          p_enabled: enabled,
        } as never
      );

      if (error) throw error;
      return { data, organizationId, clientSlug, enabled };
    },
    onSuccess: ({ clientSlug }) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['clients', clientSlug] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useClientBillingHistory(orgId: string) {
  return useQuery({
    queryKey: ['clients', orgId, 'billing'],
    queryFn: async () => {
      const { data, error } = await supabaseAdmin
        .from('billing_history')
        .select('*')
        .eq('organization_id', orgId as never)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data as unknown as BillingHistory[]) || [];
    },
    enabled: !!orgId,
  });
}

export function useClientBillingEvents(orgId: string) {
  return useQuery({
    queryKey: ['clients', orgId, 'events'],
    queryFn: async () => {
      const { data, error } = await supabaseAdmin
        .from('billing_events')
        .select('*')
        .eq('organization_id', orgId as never)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return (data as unknown as BillingEvent[]) || [];
    },
    enabled: !!orgId,
  });
}

interface UpdateLicenseResult {
  success: boolean;
  new_count: number;
  licensed_count: number;
  error_message: string | null;
  billing_skipped?: boolean;
}

export function useUpdateClientLicense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      clientId,
      clientSlug,
      resourceType,
      delta,
      skipBilling = false,
    }: {
      clientId: string;
      clientSlug: string;
      resourceType: 'location' | 'register';
      delta: number;
      skipBilling?: boolean;
    }) => {
      const { data, error } = await supabase.rpc('update_client_license' as never, {
        p_client_id: clientId,
        p_resource_type: resourceType,
        p_delta: delta,
        p_skip_billing: skipBilling,
      } as never);

      if (error) throw error;

      const results = data as unknown as UpdateLicenseResult[];
      const result = results?.[0];
      if (!result?.success) {
        throw new Error(result?.error_message || 'Failed to update license');
      }

      return { ...result, clientSlug, clientId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['clients', data.clientSlug] });
      queryClient.invalidateQueries({ queryKey: ['clients', data.clientId, 'events'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useToggleBillingExempt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      clientId,
      billingExempt,
    }: {
      clientId: string;
      billingExempt: boolean;
    }) => {
      const { data, error } = await supabase.rpc('set_billing_exempt' as never, {
        p_client_id: clientId,
        p_billing_exempt: billingExempt,
      } as never);

      if (error) throw error;
      return data as unknown as { success: boolean; billing_exempt: boolean };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useChangeBillingInterval() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      clientId,
      billingInterval,
    }: {
      clientId: string;
      billingInterval: 'monthly' | 'annual';
    }) => {
      const { data, error } = await supabase.rpc('set_billing_interval' as never, {
        p_client_id: clientId,
        p_billing_interval: billingInterval,
      } as never);

      if (error) throw error;
      return data as unknown as { success: boolean; billing_interval: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useSubscriptionPlans() {
  return useQuery({
    queryKey: ['subscription-plans'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true as never)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      return (data as unknown as SubscriptionPlan[]) || [];
    },
  });
}

export function useUpdateTrialDuration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      organizationId,
      trialEndsAt,
    }: {
      organizationId: string;
      trialEndsAt: string;
    }) => {
      const { data, error } = await supabase.rpc('update_trial_duration' as never, {
        p_organization_id: organizationId,
        p_trial_ends_at: trialEndsAt,
      } as never);

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useSyncSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ clientSlug: _clientSlug }: { clientSlug: string }) => {
      // No-op for now since admin is on the same database
      return { success: true, message: 'Subscription data is live (same database)' };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}

export function useUpdateSubscriptionStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      organizationId,
      status,
    }: {
      organizationId: string;
      status: 'trialing' | 'active' | 'past_due' | 'cancelled' | 'unpaid';
    }) => {
      const { error } = await supabase
        .from('subscription_info')
        .update({ status } as never)
        .eq('organization_id', organizationId as never);

      if (error) throw error;

      // Also update org subscription_status
      const orgStatus = status === 'trialing' ? 'trialing' : status;
      await supabase
        .from('organizations')
        .update({ subscription_status: orgStatus } as never)
        .eq('id', organizationId as never);

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useDeleteOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ organizationId }: { organizationId: string }) => {
      const { data, error } = await supabase.rpc('hard_delete_organization' as never, {
        p_organization_id: organizationId,
      } as never);

      if (error) throw error;
      return data as unknown as { success: boolean; organization_name: string; users_deleted: number };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}
